'use client';

import { useEffect, useState } from 'react';

import { ApiClientError, createApiClient, type MeResponse } from '@/lib/api';
import { formatUsdt } from '@/lib/format';
import { getInitData, hapticImpact } from '@/lib/tma';

interface PendingSummary {
  withdrawals_count: number;
  withdrawals_total_micro: number;
  deposits_count: number;
}

export function BalanceCard({
  user,
  refreshKey,
  onDeposit,
  onWithdraw,
}: {
  user: MeResponse['user'];
  refreshKey: number;
  onDeposit: () => void;
  onWithdraw: () => void;
}) {
  const [pending, setPending] = useState<PendingSummary | null>(null);

  useEffect(() => {
    const api = createApiClient(getInitData);
    api
      .myPending()
      .then((r) => setPending(r.summary))
      .catch((err: unknown) => {
        const e = err as ApiClientError;
        console.error('pending fetch failed', e?.code);
      });
  }, [refreshKey]);

  const canWithdraw = user.balances.usdt_micro >= 5_000_000;
  const availableMicro = user.balances.usdt_micro;

  return (
    <div className="mb-4">
      {/* BIG signature lime block — sliced bottom-right corner */}
      <div className="relative bg-accent text-bg-base p-5 sliced-tr">
        <div className="flex justify-between mb-1.5 font-mono text-[10px] font-bold tracking-[0.2em]">
          <span>ОБЩИЙ БАЛАНС</span>
          <span>USDT</span>
        </div>
        <div className="display text-[44px] leading-[0.9] tabular-nums">
          {formatUsdt(availableMicro)}
        </div>
        <div className="font-mono text-[10px] mt-3 opacity-70 tracking-[0.1em]">
          ОСНОВНОЙ ДЛЯ СТАВОК
        </div>
      </div>

      {/* Pending state */}
      {pending && (pending.withdrawals_count > 0 || pending.deposits_count > 0) && (
        <div className="grid grid-cols-2 gap-2 mt-2">
          {pending.withdrawals_count > 0 && (
            <div className="bg-bg-elev border-l-2 border-warn p-2.5 text-[11px]">
              <div className="font-mono text-[9px] text-text-tertiary tracking-[0.15em]">
                ⏳ ВЫВОД
              </div>
              <div className="font-bold text-warn tabular-nums">
                −{formatUsdt(pending.withdrawals_total_micro)}
              </div>
            </div>
          )}
          {pending.deposits_count > 0 && (
            <div className="bg-bg-elev border-l-2 border-text-tertiary p-2.5 text-[11px]">
              <div className="font-mono text-[9px] text-text-tertiary tracking-[0.15em]">
                ⏳ ДЕПОЗИТЫ
              </div>
              <div className="font-bold tabular-nums">
                {pending.deposits_count}
              </div>
            </div>
          )}
        </div>
      )}

      {/* balance_ton_nano раньше отображался как "💎 X TON". Это вводило в
          заблуждение: поле — это deposit-accumulator (грозит дед-letter
          accounting), withdraw debits balance_usdt_micro (USD-equivalent),
          а ton_nano НЕ decrement'ится. Юзер думал "у меня X TON выводимых",
          а реально withdrawable = balance_usdt_micro. Убрано. */}

      {/* Action buttons */}
      <div className="grid grid-cols-2 gap-2 mt-3">
        <button
          onClick={() => {
            hapticImpact('medium');
            onDeposit();
          }}
          className="py-3 bg-bg-elev text-white border border-accent display text-[12px] tracking-[0.08em] active:scale-[0.97] transition-transform"
        >
          + ПОПОЛНИТЬ
        </button>
        <button
          onClick={() => {
            hapticImpact('medium');
            onWithdraw();
          }}
          disabled={!canWithdraw}
          className="py-3 bg-transparent text-white border border-line-strong display text-[12px] tracking-[0.08em] active:scale-[0.97] transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
        >
          ↗ ВЫВЕСТИ
        </button>
      </div>
      <p className="font-mono text-[10px] text-text-tertiary mt-2 text-center tracking-[0.1em]">
        TON · USDT TRC-20
      </p>
    </div>
  );
}
